import type { IDealSummary } from '@/types/api';
import { MarkKind } from '../constants';

/**
 * The deals the map can draw, as the source expects them: placed, and in a stage the user has not
 * hidden. A deal HubSpot holds without coordinates is dropped rather than placed at 0,0 — it still
 * counts towards its zone, which the zone panel reads from the same payload.
 */
export function toDealFeatureCollection(
  deals: IDealSummary[],
  hiddenStages: readonly string[] = [],
  ownerSelection: readonly string[] = [],
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  const hidden = new Set(hiddenStages);
  const owners = new Set(ownerSelection);

  for (const deal of deals) {
    if (deal.latitude === undefined || deal.longitude === undefined) continue;
    // Hidden before clustered, so a hidden deal pulls no cluster's anchor towards itself and counts
    // towards nothing. Filtering after the fact leaves a cluster centred on members it no longer
    // admits to holding.
    if (hidden.has(deal.stage)) continue;
    if (owners.size > 0 && (deal.owner === undefined || !owners.has(deal.owner))) continue;

    features.push({
      type: 'Feature',
      // The HubSpot id, so feature-state and the expanded-ring filter address the same deal the
      // detail route does.
      id: deal.id,
      geometry: { type: 'Point', coordinates: [deal.longitude, deal.latitude] },
      properties: {
        id: deal.id,
        // The clustered source holds locations too, so the kind is what the unclustered deal
        // layers filter on and what the chip counts per kind.
        kind: MarkKind.DEAL,
        stage: deal.stage,
        ...(deal.zone_ref === undefined ? {} : { zone_ref: deal.zone_ref }),
      },
    });
  }

  return { type: 'FeatureCollection', features };
}
